import { contractPlanDate, contractPlanPercentage, contractPlanWarning } from "@/lib/contractPlanPresentation";
import { getContractErrorMessage } from "@/lib/contractErrorMessages";
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Plus } from "lucide-react";
import { toast } from "sonner";
import { CertificationCreateDialog } from "./certifications/CertificationCreateDialog";
import { CertificationDetailDialog } from "./certifications/CertificationDetailDialog";
import { MilestoneForm } from "./ContractMilestonesSection";
import { ContractMilestoneLifecycleActions } from "./ContractMilestoneLifecycleActions";
import { resolvePlannedBillingMilestoneAmount } from "@/finance/billingMilestoneAmounts";
import { hasVerifiedBillingInvoiceIdentity } from "@shared/billing/billing-due-date";
import {
  INVOICE_PAID_PENDING_ECONOMIC_REVIEW_LABEL,
  INVOICE_REVIEW_REQUIRED_LABEL,
  billingMilestoneDisplayStatus,
  isInvoicePaidPendingEconomicReview,
} from "@/finance/billingCollectionPresentation";

function money(v: string | number | null | undefined, currency = "EUR"): string {
  if (v == null) return "—";
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString("es-ES", { style: "currency", currency }) : String(v);
}

const STATUS_LABELS: Record<string, string> = {
  pending: "Pendiente",
  ready_to_invoice: "Listo para facturar",
  accounting_notified: "Contabilidad notificada",
  invoiced: "Facturado",
  pending_collection: "Pendiente de cobro",
  invoice_review: INVOICE_REVIEW_REQUIRED_LABEL,
  collected: "Cobrado",
  draft: "Borrador",
  pending_pm_review: "Pendiente revisión PM",
  pm_approved: "Aprobado PM",
  sent_to_client: "Enviado al cliente",
  client_accepted: "Aceptado por cliente",
  rejected: "Rechazado",
  cancelled: "Cancelado",
};

function milestoneCycleStatusLabel(milestone: Pick<MilestoneRow, "status">): string {
  return `Ciclo: ${STATUS_LABELS[milestone.status] ?? "Estado registrado"}`;
}

function milestoneCollectionStatusLabel(
  milestone: Pick<MilestoneRow, "status" | "balanceAmount" | "collectionStatus" | "invoiceEconomicReviewRequired" | "invoiceNumber" | "invoiceDate" | "invoiceFiscalVerified">,
): string | null {
  if (isInvoicePaidPendingEconomicReview(milestone)) {
    return `Saldo: ${INVOICE_PAID_PENDING_ECONOMIC_REVIEW_LABEL}`;
  }
  const displayStatus = billingMilestoneDisplayStatus(milestone);
  if (displayStatus === "invoice_review") {
    return `Saldo: ${INVOICE_REVIEW_REQUIRED_LABEL}`;
  }
  if (displayStatus === "pending_collection" && milestone.status !== "pending_collection") {
    return "Saldo: Pendiente de cobro";
  }
  if (displayStatus === "collected" && milestone.status !== "collected") {
    return "Saldo: Liquidado";
  }
  return null;
}

type MilestoneRow = {
  id: number;
  isRetention?: boolean;
  retentionDue?: boolean;
  label: string;
  type: string;
  status: string;
  percentage: string | null;
  fixedAmount: string | null;
  calculatedAmount: string | null;
  dueDate: string | null;
  certificationId: number | null;
  invoiceNumber?: string | null;
  invoiceDate?: Date | string | null;
  invoiceFiscalVerified?: boolean;
  invoiceAmount: string | null;
  balanceAmount?: string | null;
  collectionStatus?: string | null;
  invoiceEconomicReviewRequired?: boolean;
  requiresEconomicReview?: boolean;
  invoiceEconomicReviewMessage?: string | null;
  notes?: string | null;
};

type MilestoneType = "fixed_amount" | "percentage" | "monthly_certification";

export function ContractWorkspacePlanSection({
  contractId,
  currency = "EUR",
  canWriteEconomic = false,
  contextLabel,
}: {
  contractId: number;
  currency?: string;
  canWriteEconomic?: boolean;
  effectiveAmount?: string | null;
  contextLabel?: string;
}) {
  const utils = trpc.useUtils();
  const plan = trpc.customers.contracts.workspacePlan.useQuery({ contractId }, { enabled: !!contractId });
  const economyMilestonesQuery = trpc.customers.contracts.economyMilestones.useQuery({ contractId }, { enabled: !!contractId });
  const validation = trpc.customers.contracts.validateMilestonePlan.useQuery(
    { contractId },
    { enabled: canWriteEconomic && !!contractId },
  );
  const certCapsQuery = trpc.contractCertificationsGetMyCapabilities.useQuery();
  const certCaps = certCapsQuery.data;

  const createMilestone = trpc.customers.contracts.createMilestone.useMutation();
  const updateMilestone = trpc.customers.contracts.updateMilestone.useMutation();

  const [addOpen, setAddOpen] = useState(false);
  const [createMilestoneOpen, setCreateMilestoneOpen] = useState(false);
  const [createCertificationOpen, setCreateCertificationOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MilestoneRow | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [label, setLabel] = useState("");
  const [type, setType] = useState<MilestoneType>("percentage");
  const [percentage, setPercentage] = useState("");
  const [fixedAmount, setFixedAmount] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [isRetention, setIsRetention] = useState(false);
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  function invalidatePlan(milestoneId?: number) {
    utils.customers.contracts.workspacePlan.invalidate({ contractId });
    utils.customers.contracts.economyMilestones.invalidate({ contractId });
    utils.customers.contracts.validateMilestonePlan.invalidate({ contractId });
    utils.customers.contracts.economySummary.invalidate({ contractId });
    utils.contractCertificationsList.invalidate({ contractId });
    utils.contractCertificationsEconomicContext.invalidate({ contractId });
    if (milestoneId) {
      utils.billingMilestones.getById.invalidate({ id: milestoneId });
      utils.billingMilestones.auditLog.invalidate({ billingMilestoneId: milestoneId });
    }
  }

  function resetMilestoneForm() {
    setLabel("");
    setType("percentage");
    setIsRetention(false);
    setPercentage("");
    setFixedAmount("");
    setDueDate("");
    setNotes("");
    setFormError(null);
  }

  function openCreateMilestone() {
    resetMilestoneForm();
    setAddOpen(false);
    setCreateMilestoneOpen(true);
  }

  function openEdit(milestone: MilestoneRow) {
    setLabel(milestone.label);
    setIsRetention(milestone.isRetention ?? false);
    setType((milestone.type || "percentage") as MilestoneType);
    setPercentage(milestone.percentage ?? "");
    setFixedAmount(milestone.fixedAmount ?? "");
    setDueDate(milestone.dueDate ?? "");
    setNotes(milestone.notes ?? "");
    setFormError(null);
    setEditTarget(milestone);
  }

  async function handleCreateMilestone() {
    if (!label.trim()) {
      setFormError("El nombre del hito es obligatorio.");
      return;
    }
    setFormError(null);
    try {
      await createMilestone.mutateAsync({
        contractId,
        isRetention,
        label: label.trim(),
        type,
        percentage: type === "percentage" ? (percentage ? Number(percentage) : null) : null,
        fixedAmount: type === "fixed_amount" ? (fixedAmount || null) : null,
        dueDate: dueDate || null,
        notes: notes || null,
      });
      toast.success("Hito creado");
      setCreateMilestoneOpen(false);
      invalidatePlan();
    } catch (error) {
      setFormError(getContractErrorMessage(error));
    }
  }

  async function handleUpdateMilestone() {
    if (!editTarget) return;
    if (!label.trim()) {
      setFormError("El nombre del hito es obligatorio.");
      return;
    }
    setFormError(null);
    try {
      await updateMilestone.mutateAsync({
        milestoneId: editTarget.id,
        contractId,
        isRetention,
        label: label.trim(),
        percentage: type === "percentage" ? (percentage ? Number(percentage) : null) : null,
        fixedAmount: type === "fixed_amount" ? (fixedAmount || null) : null,
        dueDate: dueDate || null,
        notes: notes || null,
      });
      toast.success("Hito actualizado");
      setEditTarget(null);
      invalidatePlan();
    } catch (error) {
      setFormError(getContractErrorMessage(error));
    }
  }

  if (plan.isLoading || certCapsQuery.isLoading) return <Skeleton className="h-40 w-full" />;
  if (plan.isError) {
    return <Card><CardContent className="py-6 text-center text-sm text-muted-foreground">No se pudo cargar el plan de facturación.</CardContent></Card>;
  }

  const rows = plan.data ?? [];
  const canCreateCertification = certCaps?.canCreate ?? false;
  const canReadCertification = certCaps?.canRead ?? false;
  const canAdd = canWriteEconomic || canCreateCertification;
  const v = validation.data;

  const certificationDetail = detailId != null && canReadCertification ? (
    <CertificationDetailDialog
      certificationId={detailId}
      contractId={contractId}
      currency={currency}
      caps={{
        canSubmit: certCaps?.canSubmit ?? false,
        canPmReview: certCaps?.canPmReview ?? false,
        canSendToClient: certCaps?.canSendToClient ?? false,
        canClientAccept: certCaps?.canClientAccept ?? false,
        canCancel: certCaps?.canCancel ?? false,
        canEditDraft: certCaps?.canEditDraft ?? false,
        canManageDocs: certCaps?.canManageDocs ?? false,
      }}
      open
      onClose={() => setDetailId(null)}
      onMutated={invalidatePlan}
    />
  ) : null;

  return (
    <div className="space-y-3">
      {canWriteEconomic && v && !v.valid && (
        <div className="flex items-center gap-2 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{contractPlanWarning(v.warning)}</span>
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle className="text-base">Plan y facturación</CardTitle>
            <p className="text-xs text-muted-foreground">Una fila por hito o certificación; las certificaciones vinculadas no se duplican.</p>
          </div>
          {canAdd && (
            <DropdownMenu open={addOpen} onOpenChange={setAddOpen}>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="min-h-[36px]"><Plus className="mr-1 h-4 w-4" />Añadir</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canWriteEconomic && <DropdownMenuItem onSelect={openCreateMilestone}><Plus className="h-4 w-4" />Añadir hito</DropdownMenuItem>}
                {canCreateCertification && <DropdownMenuItem onSelect={() => { setAddOpen(false); setCreateCertificationOpen(true); }}><Plus className="h-4 w-4" />Añadir certificación</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No hay hitos ni certificaciones para este contrato.</p>
          ) : (
            <div className="space-y-2">
              {rows.map((row) => {
                if (row.kind === "cert_milestone") {
                  const milestone = row.milestone as MilestoneRow;
                  return (
                    <div key={`pair-${milestone.id}-${row.certification.id}`} className="rounded-md border p-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                          <div className="text-sm font-medium">{milestone.label}</div>
                          <button type="button" className="text-left text-xs text-muted-foreground underline-offset-2 hover:underline" onClick={() => canReadCertification && setDetailId(row.certification.id)}>
                            Certificación vinculada · {row.certification.label ?? `#${row.certification.id}`}
                          </button>
                          {milestone.dueDate && <div className="text-xs text-muted-foreground">{contractPlanDate(milestone.dueDate)}</div>}
                          {milestone.notes && <div className="mt-0.5 text-xs text-muted-foreground">{milestone.notes}</div>}
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-2 text-sm">
                          <div className="flex flex-col items-end gap-0.5 text-xs">
                            <span data-testid={`milestone-plan-amount-${milestone.id}`}>Planificado: {money(resolvePlannedBillingMilestoneAmount(milestone), currency)}</span>
                            {hasVerifiedBillingInvoiceIdentity(milestone) && milestone.invoiceAmount != null && <span data-testid={`milestone-invoice-amount-${milestone.id}`}>Factura exigible: {money(milestone.invoiceAmount, currency)}</span>}
                          </div>
                          <Badge variant="outline" data-testid={`milestone-cycle-status-${milestone.id}`}>{milestoneCycleStatusLabel(milestone)}</Badge>
                        {milestoneCollectionStatusLabel(milestone) && (
                          <Badge variant="secondary" data-testid={`milestone-collection-status-${milestone.id}`}>
                            {milestoneCollectionStatusLabel(milestone)}
                          </Badge>
                        )}
                          <ContractMilestoneLifecycleActions
                            milestone={milestone}
                            currency={currency}
                            contextLabel={contextLabel}
                            onMutated={invalidatePlan}
                            onEdit={canWriteEconomic && milestone.status === "pending" ? () => openEdit(milestone) : undefined}
                          />
                        </div>
                      </div>
                    </div>
                  );
                }
                if (row.kind === "milestone") {
                  const milestone = row.milestone as MilestoneRow;
                  const plannedAmount = resolvePlannedBillingMilestoneAmount(milestone);
                  return (
                    <div key={`milestone-${milestone.id}`} className="flex flex-wrap items-start justify-between gap-2 rounded-md border p-3">
                      <div className="min-w-0 flex-1 [overflow-wrap:anywhere]"><div className="text-sm font-medium">{milestone.label}</div><div className="text-xs text-muted-foreground">{contractPlanDate(milestone.dueDate)}</div>{milestone.notes && <div className="mt-0.5 text-xs text-muted-foreground">{milestone.notes}</div>}</div>
                      <div className="flex flex-wrap items-center justify-end gap-2 text-sm">
                        {milestone.percentage != null && <span>{contractPlanPercentage(milestone.percentage)}</span>}
                        <span data-testid={`milestone-plan-amount-${milestone.id}`}>Planificado: {money(plannedAmount, currency)}</span>
                        {hasVerifiedBillingInvoiceIdentity(milestone) && milestone.invoiceAmount != null && <span data-testid={`milestone-invoice-amount-${milestone.id}`}>Factura exigible: {money(milestone.invoiceAmount, currency)}</span>}
                        {milestone.isRetention && <Badge variant="secondary">Retención</Badge>}
                        {milestone.retentionDue && <span role="status" className="text-amber-800 font-medium">Retención vencida · revisar cobro</span>}
                        <Badge variant="outline" data-testid={`milestone-cycle-status-${milestone.id}`}>{milestoneCycleStatusLabel(milestone)}</Badge>
                        {milestoneCollectionStatusLabel(milestone) && (
                          <Badge variant="secondary" data-testid={`milestone-collection-status-${milestone.id}`}>
                            {milestoneCollectionStatusLabel(milestone)}
                          </Badge>
                        )}
                        <ContractMilestoneLifecycleActions
                          milestone={milestone}
                          currency={currency}
                          contextLabel={contextLabel}
                          onMutated={invalidatePlan}
                          onEdit={canWriteEconomic && milestone.status === "pending" ? () => openEdit(milestone) : undefined}
                        />
                      </div>
                    </div>
                  );
                }
                const certification = row.certification;
                return (
                  <button key={`certification-${certification.id}`} type="button" className="flex w-full flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-left hover:bg-muted/50" onClick={() => canReadCertification && setDetailId(certification.id)}>
                    <div className="min-w-0 max-w-full [overflow-wrap:anywhere]"><div className="text-sm font-medium">{certification.label ?? `Certificación #${certification.id}`}</div><div className="text-xs text-muted-foreground">Sin hito vinculado</div></div>
                    <div className="flex items-center gap-2 text-sm"><span>{money(certification.grossCertifiedAmount, currency)}</span><Badge variant="outline">{STATUS_LABELS[certification.status] ?? "Estado registrado"}</Badge></div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {(() => {
        const cancelledMilestones = (economyMilestonesQuery.data ?? []).filter((m) => m.status === "cancelled");
        if (cancelledMilestones.length === 0) return null;
        return (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm font-medium">Historial y cancelados ({cancelledMilestones.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {cancelledMilestones.map((m) => (
                <div key={`cancelled-${m.id}`} className="flex flex-wrap items-start justify-between gap-2 rounded-md border p-3 bg-muted/20">
                  <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                    <div className="text-sm font-medium line-through text-muted-foreground">{m.label}</div>
                    <div className="text-xs text-muted-foreground">{contractPlanDate(m.dueDate)}</div>
                    {m.notes && <div className="mt-0.5 text-xs text-muted-foreground">{m.notes}</div>}
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2 text-sm">
                    <span data-testid={"milestone-plan-amount-" + m.id} className="text-muted-foreground">Planificado: {money(resolvePlannedBillingMilestoneAmount(m), currency)}</span>
                    {hasVerifiedBillingInvoiceIdentity(m) && m.invoiceAmount != null && <span data-testid={"milestone-invoice-amount-" + m.id} className="text-muted-foreground">Factura exigible: {money(m.invoiceAmount, currency)}</span>}
                    {m.isRetention && <Badge variant="outline">Retención</Badge>}
                    <Badge variant="secondary">Cancelado</Badge>
                    <ContractMilestoneLifecycleActions
                      milestone={m}
                      currency={currency}
                      contextLabel={contextLabel}
                      onMutated={invalidatePlan}
                      isHistorical
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })()}

      <Dialog open={createMilestoneOpen} onOpenChange={setCreateMilestoneOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuevo hito</DialogTitle></DialogHeader>
          <MilestoneForm lockType={!!editTarget} isRetention={isRetention} setIsRetention={setIsRetention} label={label} setLabel={setLabel} type={type} setType={setType} percentage={percentage} setPercentage={setPercentage} fixedAmount={fixedAmount} setFixedAmount={setFixedAmount} dueDate={dueDate} setDueDate={setDueDate} notes={notes} setNotes={setNotes} error={formError} />
          <DialogFooter><Button variant="outline" onClick={() => setCreateMilestoneOpen(false)}>Cancelar</Button><Button disabled={createMilestone.isPending} onClick={handleCreateMilestone}>{createMilestone.isPending ? "Guardando..." : "Crear hito"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={(open) => { if (!open) setEditTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Editar hito</DialogTitle></DialogHeader>
          <MilestoneForm lockType={!!editTarget} isRetention={isRetention} setIsRetention={setIsRetention} label={label} setLabel={setLabel} type={type} setType={setType} percentage={percentage} setPercentage={setPercentage} fixedAmount={fixedAmount} setFixedAmount={setFixedAmount} dueDate={dueDate} setDueDate={setDueDate} notes={notes} setNotes={setNotes} error={formError} />
          <DialogFooter><Button variant="outline" onClick={() => setEditTarget(null)}>Cancelar</Button><Button disabled={updateMilestone.isPending} onClick={handleUpdateMilestone}>{updateMilestone.isPending ? "Guardando..." : "Guardar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <CertificationCreateDialog contractId={contractId} open={createCertificationOpen} onClose={() => setCreateCertificationOpen(false)} onCreated={() => { setCreateCertificationOpen(false); invalidatePlan(); }} />
      {certificationDetail}
    </div>
  );
}
